import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Send,
  Calendar,
  Inbox,
  Sparkles,
  Image,
  BarChart2,
  Telescope,
  Settings,
  ChevronLeft,
  ChevronRight,
  Clock,
  Upload,
  LogOut,
  Shield,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores/appStore';
import { AgencyWorkspaceSwitcher } from '@/components/agency/AgencyWorkspaceSwitcher';
import { useUser, useAuth } from '@/lib/auth';
import { Logo } from '@/components/ui/Logo';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/publishing', icon: Send, label: 'Publishing' },
  { to: '/calendar', icon: Calendar, label: 'Calendar' },
  { to: '/queues', icon: Clock, label: 'Queues' },
  { to: '/bulk-posting', icon: Upload, label: 'Bulk Posting' },
  { to: '/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/ai-studio', icon: Sparkles, label: 'AI Studio' },
  { to: '/media', icon: Image, label: 'Media Library' },
  { to: '/analytics', icon: BarChart2, label: 'Analytics' },
  { to: '/research', icon: Telescope, label: 'Creator Research' },
];

/** Nav item: a pill, accent-tinted when active (S-FX control rule). */
function navItemClass(isActive: boolean, collapsed: boolean) {
  return cn(
    'flex h-9 items-center gap-3 rounded-full px-3 text-sm font-medium transition-colors duration-fast ease-sfx',
    isActive
      ? 'bg-accent/10 text-foreground ring-1 ring-inset ring-accent/25'
      : 'text-muted-foreground hover:bg-tertiary/60 hover:text-foreground',
    collapsed && 'justify-center px-0'
  );
}

interface SidebarProps {
  /** Drawer mode (small screens): always expanded, the chevron closes the drawer. */
  forceExpanded?: boolean;
}

export function Sidebar({ forceExpanded }: SidebarProps) {
  const { sidebarCollapsed: storedCollapsed, setSidebarCollapsed, setMobileNavOpen } = useAppStore();
  const sidebarCollapsed = forceExpanded ? false : storedCollapsed;
  const { user } = useUser();
  const { signOut } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close the user menu when clicking outside of it.
  useEffect(() => {
    if (!userMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  return (
    <aside
      className={cn(
        'flex h-[100dvh] shrink-0 flex-col border-r border-hairline bg-sidebar transition-[width] duration-base ease-sfx',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center justify-between px-4">
        {!sidebarCollapsed && <Logo />}
        {sidebarCollapsed && <Logo variant="mark" className="mx-auto" />}
        {!sidebarCollapsed && (
          <button
            type="button"
            onClick={() => (forceExpanded ? setMobileNavOpen(false) : setSidebarCollapsed(true))}
            className="rounded-full p-1.5 text-muted-foreground transition-colors duration-fast hover:bg-tertiary/70 hover:text-foreground"
            aria-label={forceExpanded ? 'Close navigation' : 'Collapse sidebar'}
          >
            {forceExpanded ? <X className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Agency/Workspace Switcher */}
      {!sidebarCollapsed && (
        <div className="px-3 pb-3" data-tour="workspace-switcher">
          <AgencyWorkspaceSwitcher />
        </div>
      )}

      {/* Navigation */}
      <nav aria-label="Main" className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
        {!sidebarCollapsed && (
          <p className="px-3 pb-2 text-[11px] font-medium text-muted-foreground">Workspace</p>
        )}
        {NAV_ITEMS.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            title={sidebarCollapsed ? label : undefined}
            data-tour={`nav-${to === '/' ? 'dashboard' : to.slice(1)}`}
            className={({ isActive }) => navItemClass(isActive, sidebarCollapsed)}
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={cn('h-4 w-4 shrink-0', isActive && 'text-accent')}
                  strokeWidth={isActive ? 2.25 : 2}
                />
                {!sidebarCollapsed && <span className="truncate">{label}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom: Admin (superadmin only) + Settings + User */}
      <div className="space-y-0.5 border-t border-hairline px-3 py-3">
        {user?.isSuperadmin && (
          <NavLink
            to="/admin"
            title={sidebarCollapsed ? 'Admin' : undefined}
            className={({ isActive }) => navItemClass(isActive, sidebarCollapsed)}
          >
            <Shield className="h-4 w-4 shrink-0 text-accent" />
            {!sidebarCollapsed && <span>Admin</span>}
          </NavLink>
        )}
        <NavLink
          to="/settings"
          title={sidebarCollapsed ? 'Settings' : undefined}
          data-tour="nav-settings"
          className={({ isActive }) => navItemClass(isActive, sidebarCollapsed)}
        >
          {({ isActive }) => (
            <>
              <Settings className={cn('h-4 w-4 shrink-0', isActive && 'text-accent')} />
              {!sidebarCollapsed && <span>Settings</span>}
            </>
          )}
        </NavLink>

        {/* User avatar + menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((o) => !o)}
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            className={cn(
              'mt-1 flex w-full items-center gap-3 rounded-l px-2.5 py-2 text-left transition-colors duration-fast ease-sfx',
              !sidebarCollapsed && 'bg-tertiary/40 ring-1 ring-inset ring-hairline hover:bg-tertiary/70',
              sidebarCollapsed && 'justify-center px-2 hover:bg-tertiary/60'
            )}
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-accent">
              <span className="text-xs font-semibold text-primary">
                {user?.firstName?.[0] ?? user?.emailAddresses[0]?.emailAddress?.[0]?.toUpperCase() ?? 'U'}
              </span>
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-foreground">
                  {user?.fullName ?? user?.emailAddresses[0]?.emailAddress ?? 'User'}
                </p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {user?.emailAddresses[0]?.emailAddress ?? ''}
                </p>
              </div>
            )}
          </button>

          {userMenuOpen && (
            <div
              role="menu"
              className={cn(
                'sfx-pop sfx-pop-up absolute bottom-full z-50 mb-2 overflow-hidden rounded-l bg-popover shadow-kumo-xl ring-1 ring-hairline',
                sidebarCollapsed ? 'left-0 w-44' : 'left-0 right-0'
              )}
            >
              <div className="p-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    void signOut();
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-danger transition-colors duration-fast hover:bg-danger/10"
                >
                  <LogOut className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-xs font-medium">Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expand button when collapsed */}
      {sidebarCollapsed && (
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={() => setSidebarCollapsed(false)}
            className="flex w-full items-center justify-center rounded-full p-2 text-muted-foreground transition-colors duration-fast hover:bg-tertiary/60 hover:text-foreground"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </aside>
  );
}
